import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import * as Sentry from '@sentry/react';
import React from 'react';

import { format } from 'src/lib/format';
import { SetGcus, SetGcusVariables } from 'src/lib/graphqlTypes/types';

import { generateRange } from './helpers/generateRange';

const setGcusMutation = gql`
  mutation SetGcus($gcus: Int!, $routerId: ID!) {
    cloud {
      router(id: $routerId) {
        setGcus(gcus: $gcus) {
          ... on RouterGcusSuccess {
            order {
              id
              status
            }
          }
          ... on InvalidInputErrors {
            errors {
              argument
              location
              reason
            }
            message
          }
          ... on InternalServerError {
            message
          }
        }
      }
    }
  }
`;
export const SetGcusModal = ({
  gcus,
  routerId,
  isOpen,
  onClose,
  onUpdate,
}: {
  gcus: number;
  routerId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}) => {
  const [success, setSuccess] = React.useState<boolean>(false);
  const [updateGcus, { data, loading, error }] = useMutation<
    SetGcus,
    SetGcusVariables
  >(setGcusMutation, {
    variables: {
      routerId,
      gcus,
    },
    onCompleted: () => {
      if (data?.cloud?.router?.setGcus?.__typename === 'RouterGcusSuccess') {
        Sentry.captureMessage('Successfully updated router GCUs', 'info');
        setSuccess(true);
      }
      onUpdate();
      setTimeout(() => {
        onClose();
      }, 2000);
    },
    onError: (e) => {
      Sentry.captureException(e);
    },
  });

  const [gcuValue, setGcuValue] = React.useState<string>(`${gcus}`);

  const gcuRange = generateRange(['1', '5']);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // Capture the newly selected value and update component state
    setGcuValue(e.target.value);
  };

  const handleUpdate = (value: string) => {
    updateGcus({
      variables: {
        routerId,
        gcus: parseInt(value, 10),
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Manage Capacity</ModalTitle>
          <ModalDescription>
            Increase or decrease the number of{' '}
            <span className="font-semibold">Graph Compute Units (GCUs)</span>{' '}
            for this Cloud Router.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {error && (
            <p className="text-red-dark">
              We were unable to update the router capacity. Please try again
              shortly.
            </p>
          )}
          {success && (
            <p className="font-semibold">
              <span className="mr-1">{`\u2705`}</span> Your router capacity has
              been updated.
            </p>
          )}
          <FormControl>
            <FormLabel>Capacity</FormLabel>
            <Select
              className="w-64"
              id="cloudProviderRegion"
              name="cloudProviderRegion"
              onChange={handleSelectChange}
              renderTriggerNode={() => (
                <>{`${gcuValue} ${format.pluralize(
                  parseInt(gcuValue, 10),
                  'GCU',
                )}`}</>
              )}
              value={gcuValue}
            >
              {gcuRange?.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              isDisabled={!!error}
              isLoading={loading}
              loadingText="Saving Changes"
              onClick={() => {
                handleUpdate(gcuValue);
              }}
            >
              Save Changes
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
